import React, { useState, useEffect, useCallback } from "react";
import { companyApi, Company } from "../../../services/companyApi";
import {
  availabilityApi,
  AvailabilityData,
} from "../../../services/availabilityManagementApi";
import { useCompany } from "../../../contexts/CompanyContext";
import { authService } from "../../../services";
import { useToaster } from "../../shared/Toaster";
import { LoadingState } from "./LoadingState";
import { CompanyInfo } from "./CompanyInfo";
import { Availability } from "./Availability";
import { AdminPageHeader } from "../shared";
import { Icons } from "../../shared/Icons";
import { cleanPhoneNumber } from "../../../utils/phoneUtils";
import { cleanCnpj, displayCnpj, normalizeInstagramUrl } from "../../../utils/cnpjUtils";
import { ResetPasswordModal } from "./ResetPasswordModal";
import { ActionButton } from "../../shared/ActionButton";

interface CompanyFormData {
  name: string;
  email: string;
  phoneNumber: string;
  address: string;
  cnpj: string;
  instagramUrl: string;
  logo?: string;
  signature?: string;
}

export const AdminSettings: React.FC = () => {
  const { setCompany: setContextCompany } =
    useCompany();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const { showToast } = useToaster();

  // Company form state
  const [companyData, setCompanyData] = useState<CompanyFormData>({
    name: "",
    email: "",
    phoneNumber: "",
    address: "",
    cnpj: "",
    instagramUrl: "",
    logo: "",
    signature: "",
  });

  // Availability state (will be loaded from API)
  const [availability, setAvailability] = useState<AvailabilityData>({
    monday: { open: false, intervals: [] },
    tuesday: { open: false, intervals: [] },
    wednesday: { open: false, intervals: [] },
    thursday: { open: false, intervals: [] },
    friday: { open: false, intervals: [] },
    saturday: { open: false, intervals: [] },
    sunday: { open: false, intervals: [] },
  });

  const fetchAvailability = useCallback(async () => {
    try {
      const response = await availabilityApi.getAvailabilityByCompany();
      if (response.success && response.data) {
        setAvailability(response.data);
      } else {
        showToast(
          "error",
          response.error || "Falha ao carregar horários de funcionamento"
        );
      }
    } catch (err) {
      showToast("error", "Falha ao carregar horários de funcionamento");
    }
  }, [showToast]);

  const fetchCompanyData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await companyApi.getCompany();
      if (response.success && response.data) {
        setCompany(response.data);
        const companyData = response.data;
        setCompanyData({
          name: companyData.name || "",
          email: companyData.email || "",
          phoneNumber: companyData.phoneNumber || "",
          address: companyData.address || "",
          cnpj: companyData.cnpj ? displayCnpj(companyData.cnpj) : "",
          instagramUrl: companyData.instagramUrl || "",
          logo: (companyData as any).logo || "",
          signature: (companyData as any).signature || "",
        });
      } else {
        showToast(
          "error",
          response.error || "Falha ao carregar dados da empresa"
        );
      }
    } catch (err) {
      showToast("error", "Falha ao carregar dados da empresa");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchCompanyData();
    fetchAvailability();
  }, [fetchCompanyData, fetchAvailability]);

  const handleSaveCompany = async () => {
    setSaving(true);

    try {
      // Clean phone number before sending to API
      const cleanedCompanyData: any = {
        ...companyData,
        phoneNumber: cleanPhoneNumber(companyData.phoneNumber),
        cnpj: cleanCnpj(companyData.cnpj) || null,
        instagramUrl: companyData.instagramUrl.trim()
          ? normalizeInstagramUrl(companyData.instagramUrl)
          : null,
      };
      
      // Only include logo if it's set (not empty)
      if (!cleanedCompanyData.logo) {
        delete cleanedCompanyData.logo;
      }

      if (cleanedCompanyData.signature === "") {
        cleanedCompanyData.signature = null;
      } else if (!cleanedCompanyData.signature) {
        delete cleanedCompanyData.signature;
      }

      const response = await companyApi.updateCompany(
        cleanedCompanyData
      );
      if (response.success) {
        showToast("success", "Informações da empresa salvas com sucesso!");

        // Update localStorage with new company data
        authService.updateCompanyData(cleanedCompanyData);

        // Refresh local company data
        await fetchCompanyData();

        // Update context to refresh navbar and other components
        const updatedCompanyData = authService.getCompanyData();
        if (updatedCompanyData) {
          setContextCompany({
            id: updatedCompanyData.id,
            name: updatedCompanyData.name,
            email: updatedCompanyData.email,
            category: updatedCompanyData.category,
            phoneNumber: updatedCompanyData.phoneNumber,
            address: updatedCompanyData.address,
            cnpj: updatedCompanyData.cnpj,
            instagramUrl: updatedCompanyData.instagramUrl,
            logo: (updatedCompanyData as any).logo || undefined,
            signature: (updatedCompanyData as any).signature || undefined,
          });
        }
      } else {
        showToast(
          "error",
          response.error || "Falha ao salvar informações da empresa"
        );
      }
    } catch (err) {
      showToast("error", "Falha ao salvar informações da empresa");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAvailability = async () => {
    setSaving(true);

    try {
      const response = await availabilityApi.updateAvailabilityByCompany(
        availability
      );
      if (response.success) {
        showToast("success", "Horários de funcionamento salvos com sucesso!");
        await fetchAvailability(); // Refresh data
      } else {
        showToast(
          "error",
          response.error || "Falha ao salvar horários de funcionamento"
        );
      }
    } catch (err) {
      showToast("error", "Falha ao salvar horários de funcionamento");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<Icons.Settings sx={{ fontSize: 28 }} />}
        title="Configurações"
        subtitle="Gerencie configurações do sistema e preferências"
        rightContent={
          <ActionButton
            variant="outline"
            size="sm"
            onClick={() => setShowResetPasswordModal(true)}
            icon={<Icons.Lock sx={{ fontSize: 18 }} />}
          >
            Alterar Senha
          </ActionButton>
        }
      />

      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className={company?.category === "car" ? "" : "lg:col-span-2"}>
          <CompanyInfo
            companyData={companyData}
            setCompanyData={setCompanyData}
            onSave={handleSaveCompany}
            saving={saving}
            currentLogo={company?.logo}
            currentSignature={company?.signature ?? undefined}
          />
        </div>

        {company?.category === "car" && (
          <Availability
            availability={availability}
            setAvailability={setAvailability}
            onSave={handleSaveAvailability}
          />
        )}
      </div>
    </div>
  );
};
